import React, { useState, useEffect } from 'react'
import NavItem from 'components/NavItem'
import DropdownNavItem from 'components/DropdownNavItem'
import Icon from 'components/Icon'
import { Link, useLocation } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import messages, { servicesItems } from './messages'
import languages from 'data/languages/list'
import languageMessages from 'data/languages/messages'
import { messages as routeMessages } from 'routes'
import { supportedLocales } from 'i18n'
import Logo from 'components/Logo'
import { canonicals, findRouteMatch } from 'containers/Canonicals'
import useUrl from 'useUrl'
import useUtmUrl from 'useUtmUrl'
import useCurrentUser from 'hooks/useCurrentUser'
import { useBrand } from 'contexts/BrandContext'

const Nav = ({ transparent = false, fixed = true }) => {
  const [open, setOpen] = useState(false)
  const toggle = () => setOpen(!open)
  const { formatMessage, locale } = useIntl()
  const brand = useBrand()
  const location = useLocation()
  const match = findRouteMatch(location.pathname, locale)

  const {
    id,
    name = 'Anon',
    email,
    image,
    onLogOut,
    onLogIn,
  } = useCurrentUser()
  const initials = name
    .split(' ')
    .map((part) => part[0])
    .join('')

  return (
    <nav
      className={`${fixed ? 'fixed inset-x-0 z-50' : ''}  ${
        transparent ? 'border-b border-black border-opacity-25' : 'bg-primary'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto flex items-center justify-between font-medium px-5 flex-nowrap">
        <div className="flex items-center flex-shrink-0 text-white mr-6 py-4">
          <a
            href={locale === 'en' ? '/' : `/${locale}`}
            aria-label={formatMessage(messages.explore)}
          >
            <Logo />
          </a>
        </div>

        <div className="block lg:hidden">
          <button
            className="flex items-center p-4 text-white outline-none bg-black bg-opacity-25"
            onClick={toggle}
          >
            <Icon icon="bars" title="Menu" />
          </button>
        </div>

        <div
          className={`w-full block flex-grow lg:flex lg:items-center lg:w-auto sm:hidden ${
            open ? 'block' : 'hidden'
          }`}
        >
          <div className="text-sm pr-8 md:pl-0 lg:flex-grow lg:flex lg:items-center lg:justify-center">
            <DropdownNavItem
              cols={1}
              items={[...Array(Object.keys(servicesItems).length / 2)].map(
                (_, i) => (
                  <a href={formatMessage(servicesItems[`${i + 1}.href`])}>
                    <FormattedMessage {...servicesItems[`${i + 1}.title`]} />
                  </a>
                )
              )}
            >
              <a href={formatMessage(messages.servicesHref)}>
                <FormattedMessage {...messages.services} />
              </a>
            </DropdownNavItem>

            <NavItem>
              <a href={formatMessage(messages.casesHref)}>
                <FormattedMessage {...messages.cases} />
              </a>
            </NavItem>

            <NavItem>
              <a href={formatMessage(messages.pricingHref)}>
                <FormattedMessage {...messages.pricing} />
              </a>
            </NavItem>

            <DropdownNavItem
              cols={1}
              items={[
                <a href={formatMessage(messages.applyHref)}>
                  <FormattedMessage {...messages.apply} />
                </a>,
                <a href={formatMessage(messages.aboutUsHref)}>
                  <FormattedMessage {...messages.aboutUs} />
                </a>,
              ]}
            >
              <a href={formatMessage(messages.contactHref)}>
                <FormattedMessage {...messages.contact} />
              </a>
            </DropdownNavItem>

            {image ? (
              <div
                className="block rounded-full w-12 h-12 bg-primary bg-cover ring ring-2 ring-white overflow-hidden group mx-4 cursor-pointer"
                style={{ backgroundImage: `url(${image})` }}
                onClick={onLogOut}
              />
            ) : (
              <NavItem>
                <button onClick={onLogIn}>
                  <FormattedMessage {...messages.logIn} />
                </button>
              </NavItem>
            )}
          </div>

          <div className="flex justify-start items-center">
            <NavItem>
              <a href={useUrl(locale, formatMessage(routeMessages.explore))}>
                <Icon icon="search" className="text-xl" />
              </a>
            </NavItem>
            <div className="">
              <DropdownNavItem
                cols={1}
                items={Object.entries(canonicals(match, locale, brand)).map(
                  ([alternateLocale, href]) => (
                    <a href={href} key={alternateLocale} className="">
                      {supportedLocales[alternateLocale]}
                    </a>
                  )
                )}
              >
                {supportedLocales[locale]}
              </DropdownNavItem>
            </div>
            <div className="hidden md:inline-block py-6 px-3 bg-black bg-opacity-50 hover:bg-opacity-100 hover:bg-white text-white hover:text-primary transition duration-150 ease-in-out cursor-pointer">
              <a href={formatMessage(messages.contactHref)}>
                <FormattedMessage {...messages.contactUs} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}

export default Nav
